import { z } from 'zod';

import { advancedConfigurationSchema } from './advanced-config.schema';
import { toolSchema } from './tool.schema';
import { conversationFlowSchema } from './conversation-flow.schema';
export { conversationFlowSchema } from './conversation-flow.schema';
export type { ConversationFlowPolicy } from './conversation-flow.schema';

export const agentNodeSchema = z.object({
  id: z.string().uuid(),
  name: z
    .string()
    .trim()
    .min(1)
    .max(50)
    .regex(/^[a-z][a-z0-9_]*$/, 'Name must be snake_case (e.g., billing_agent)'),
  role: z.string().trim().min(2).max(200),
  type: z.enum(['ai_agent', 'human']),
  is_entry: z.boolean().default(false),
  prompt_template: z.string().min(10).optional(),
  tools: z.array(toolSchema).optional(),
  custom_tool_ids: z.array(z.string().uuid()).optional(),
  voice_name: z.string().max(100).optional(),
  advanced: advancedConfigurationSchema.optional(),
  handoff_conditions: z.string().max(2000).optional(),
  transfer_target_key: z.string().max(50).optional(),
  position: z.object({ x: z.number(), y: z.number() }),
});

export type AgentNode = z.infer<typeof agentNodeSchema>;

export const agentEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  description: z.string().max(500).default(''),
  /** Tools that must have succeeded before this transfer is allowed. */
  requires_tools: z.array(z.string().min(1)).max(10).optional(),
});

export type AgentEdge = z.infer<typeof agentEdgeSchema>;

/** Structural shape only — no cross-node refinements. Uses passthrough on
 *  node/edge schemas so future fields stored in JSON are never stripped.
 *  Used where stored graphs are re-read (snapshots/commits) so old data
 *  is never rejected. */
export const agentGraphShapeSchema = z.object({
  agents: z.array(agentNodeSchema.passthrough()),
  edges: z.array(agentEdgeSchema.passthrough()),
  conversation_flow: conversationFlowSchema.optional(),
});

export const agentGraphSchema = z
  .object({
    agents: z.array(agentNodeSchema).min(1),
    edges: z.array(agentEdgeSchema),
    conversation_flow: conversationFlowSchema.optional(),
  })
  .refine((graph) => graph.agents.filter((a) => a.is_entry).length === 1, {
    message: 'Exactly one agent must be marked as entry',
  })
  .refine(
    (graph) => {
      const names = graph.agents.map((a) => a.name);
      return new Set(names).size === names.length;
    },
    { message: 'Agent names must be unique' },
  )
  .refine(
    (graph) => {
      const entry = graph.agents.find((a) => a.is_entry);
      if (!entry) return false;
      // Skip check when no edges exist (work-in-progress graph)
      if (graph.edges.length === 0) return true;
      return graph.edges.some((e) => e.source === entry.name);
    },
    { message: 'Entry agent must have at least one transfer target' },
  )
  .refine((graph) => !graph.edges.some((e) => e.source === e.target), {
    message: 'Self-loop edges are not allowed',
  })
  .refine(
    (graph) => {
      const edgeKeys = graph.edges.map((e) => `${e.source}->${e.target}`);
      return new Set(edgeKeys).size === edgeKeys.length;
    },
    { message: 'Duplicate edges are not allowed' },
  )
  .superRefine((graph, ctx) => {
    const flow = graph.conversation_flow;
    if (!flow) return;
    const roles = new Set(graph.agents.filter((a) => a.type === 'ai_agent').map((a) => a.name));
    for (const owner of [
      ...flow.modules.map((m) => m.owner),
      ...Object.keys(flow.handoff_requirements),
    ]) {
      if (!roles.has(owner))
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Unknown conversation-flow agent: ${owner}`,
        });
    }
  });

export type AgentGraph = z.infer<typeof agentGraphSchema>;
